import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PlusCircle, Trash2 } from 'lucide-react';
import { useEffect, useMemo } from 'react';

const TCEBPInspectionFields = ({ findings, setFindings, application }) => {
   // Chrome detection for select input
   const isChrome = useMemo(() => {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isBrave = navigator.brave !== undefined;
      return userAgent.includes('chrome') && !userAgent.includes('edg') && !isBrave;
   }, []);

   // Determine if this is a cutting or earth-balling request
   const isCuttingRequest = useMemo(() => {
      return application?.isRequestForCutting || application?.requestType === 'Cutting';
   }, [application]);

   // Initialize empty tree entry
   const emptyTreeEntry = useMemo(() => ({
      species: '',
      numberOfTreesInventoried: 0,
      status: isCuttingRequest ? 'For Cutting' : 'For Earth-balling',
      treeCategory: 'Natural',
      grossVolume: isCuttingRequest ? 0 : undefined,
      netVolume: isCuttingRequest ? 0 : undefined,
      autoSetFromInventory: application?.requestType === 'Inventory'
   }), [application?.requestType, isCuttingRequest]);

   // Debug log for application data
   useEffect(() => {
      console.log('TCEBP Application Data:', {
         requestType: application?.requestType,
         isRequestForCutting: application?.isRequestForCutting,
         isRequestForEarthBalling: application?.isRequestForEarthBalling,
         currentStatus: emptyTreeEntry.status,
         isCuttingRequest: isCuttingRequest
      });
   }, [application, emptyTreeEntry, isCuttingRequest]);

   // Debug log for new tree entries
   useEffect(() => {
      console.log('TCEBP Tree Entry Status:', {
         status: emptyTreeEntry.status,
         autoSet: emptyTreeEntry.autoSetFromInventory
      });
   }, [emptyTreeEntry.status, emptyTreeEntry.autoSetFromInventory]);

   // Add new tree entry
   const addTreeEntry = () => {
      setFindings(prev => ({
         ...prev,
         tcebpFindings: {
            ...prev.tcebpFindings || {},
            treeInventory: [...(prev.tcebpFindings?.treeInventory || []), { ...emptyTreeEntry }]
         }
      }));
      updateTotals();
   };

   // Remove tree entry
   const removeTreeEntry = (index) => {
      if ((findings.tcebpFindings?.treeInventory || []).length <= 1) {
         return; // Prevent removing the last entry
      }
      setFindings(prev => ({
         ...prev,
         tcebpFindings: {
            ...prev.tcebpFindings || {},
            treeInventory: prev.tcebpFindings?.treeInventory.filter((_, i) => i !== index)
         }
      }));
      updateTotals();
   };

   // Update tree entry
   const updateTreeEntry = (index, field, value) => {
      setFindings(prev => {
         const newFindings = {
            ...prev,
            tcebpFindings: {
               ...prev.tcebpFindings || {},
               treeInventory: [...(prev.tcebpFindings?.treeInventory || [])]
            }
         };

         // If status is being updated and it's an auto-set inventory request, prevent the change
         if (field === 'status' && newFindings.tcebpFindings.treeInventory[index].autoSetFromInventory) {
            return prev;
         }

         // Handle different field types
         let processedValue = value;
         if (['numberOfTreesInventoried', 'grossVolume', 'netVolume'].includes(field)) {
            processedValue = parseFloat(value) || 0;
         }

         // Update the specific field
         newFindings.tcebpFindings.treeInventory[index] = {
            ...newFindings.tcebpFindings.treeInventory[index],
            [field]: processedValue
         };

         // Calculate totals
         updateTotalsForFindings(newFindings);

         return newFindings;
      });
   };

   // Update totals for a given findings object
   const updateTotalsForFindings = (findings) => {
      const totalTrees = findings.tcebpFindings.treeInventory.reduce(
         (sum, tree) => sum + (tree.numberOfTreesInventoried || 0),
         0
      );

      findings.tcebpFindings.totalTrees = totalTrees;

      // Only calculate volumes for cutting requests
      if (isCuttingRequest) {
         const totalGrossVolume = findings.tcebpFindings.treeInventory.reduce(
            (sum, tree) => sum + (tree.grossVolume || 0),
            0
         );

         const totalNetVolume = findings.tcebpFindings.treeInventory.reduce(
            (sum, tree) => sum + (tree.netVolume || 0),
            0
         );

         findings.tcebpFindings.totalGrossVolume = totalGrossVolume;
         findings.tcebpFindings.totalNetVolume = totalNetVolume;
      }

      return findings;
   };

   // Update totals
   const updateTotals = () => {
      setFindings(prev => {
         const newFindings = {
            ...prev,
            tcebpFindings: {
               ...prev.tcebpFindings
            }
         };

         return updateTotalsForFindings(newFindings);
      });
   };

   // Render column headers based on request type
   const renderTableHeaders = () => {
      if (isCuttingRequest) {
         return (
            <TableRow>
               <TableHead>Name of Species</TableHead>
               <TableHead>No. of Trees Inventoried</TableHead>
               <TableHead>Gross Volume (cu.m)</TableHead>
               <TableHead>Net Volume (cu.m)</TableHead>
               <TableHead>Tree Category</TableHead>
               <TableHead className="w-[50px]"></TableHead>
            </TableRow>
         );
      } else {
         return (
            <TableRow>
               <TableHead>Name of Species</TableHead>
               <TableHead>No. of Trees Inventoried</TableHead>
               <TableHead>Status</TableHead>
               <TableHead>Tree Category</TableHead>
               <TableHead className="w-[50px]"></TableHead>
            </TableRow>
         );
      }
   };

   // Render table rows for each tree entry
   const renderTreeEntryRow = (tree, index) => {
      if (isCuttingRequest) {
         return (
            <TableRow key={index}>
               <TableCell>
                  <Input
                     value={tree.species || ''}
                     onChange={(e) => updateTreeEntry(index, 'species', e.target.value)}
                     placeholder="Enter species"
                  />
               </TableCell>
               <TableCell>
                  <Input
                     type="number"
                     value={tree.numberOfTreesInventoried || ''}
                     onChange={(e) => updateTreeEntry(index, 'numberOfTreesInventoried', e.target.value)}
                     placeholder="0"
                  />
               </TableCell>
               <TableCell>
                  <Input
                     type="number"
                     step="0.001"
                     value={tree.grossVolume || ''}
                     onChange={(e) => updateTreeEntry(index, 'grossVolume', e.target.value)}
                     placeholder="0.000"
                  />
               </TableCell>
               <TableCell>
                  <Input
                     type="number"
                     step="0.001"
                     value={tree.netVolume || ''}
                     onChange={(e) => updateTreeEntry(index, 'netVolume', e.target.value)}
                     placeholder="0.000"
                  />
               </TableCell>
               <TableCell>
                  {isChrome ? (
                     <select
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        value={tree.treeCategory || 'Natural'}
                        onChange={(e) => updateTreeEntry(index, 'treeCategory', e.target.value)}
                     >
                        <option value="Natural">Natural</option>
                        <option value="Planted">Planted</option>
                     </select>
                  ) : (
                     <Select
                        value={tree.treeCategory || 'Natural'}
                        onValueChange={(value) => updateTreeEntry(index, 'treeCategory', value)}
                     >
                        <SelectTrigger>
                           <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                           <SelectItem value="Natural">Natural</SelectItem>
                           <SelectItem value="Planted">Planted</SelectItem>
                        </SelectContent>
                     </Select>
                  )}
               </TableCell>
               <TableCell>
                  <Button
                     variant="ghost"
                     size="icon"
                     onClick={() => removeTreeEntry(index)}
                  >
                     <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
               </TableCell>
            </TableRow>
         );
      } else {
         return (
            <TableRow key={index}>
               <TableCell>
                  <Input
                     value={tree.species || ''}
                     onChange={(e) => updateTreeEntry(index, 'species', e.target.value)}
                     placeholder="Enter species"
                  />
               </TableCell>
               <TableCell>
                  <Input
                     type="number"
                     value={tree.numberOfTreesInventoried || ''}
                     onChange={(e) => updateTreeEntry(index, 'numberOfTreesInventoried', e.target.value)}
                     placeholder="0"
                  />
               </TableCell>
               <TableCell>
                  {isChrome ? (
                     <select
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        value={tree.status || 'For Earth-balling'}
                        onChange={(e) => updateTreeEntry(index, 'status', e.target.value)}
                        disabled={tree.autoSetFromInventory}
                        title={tree.autoSetFromInventory ? "Status is auto-set from inventory request" : ""}
                     >
                        <option value="For Earth-balling">For Earth-balling</option>
                        <option value="For Cutting">For Cutting</option>
                     </select>
                  ) : (
                     <Select
                        value={tree.status || 'For Earth-balling'}
                        onValueChange={(value) => updateTreeEntry(index, 'status', value)}
                        disabled={tree.autoSetFromInventory}
                     >
                        <SelectTrigger title={tree.autoSetFromInventory ? "Status is auto-set from inventory request" : ""}>
                           <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                           <SelectItem value="For Earth-balling">For Earth-balling</SelectItem>
                           <SelectItem value="For Cutting">For Cutting</SelectItem>
                        </SelectContent>
                     </Select>
                  )}
               </TableCell>
               <TableCell>
                  {isChrome ? (
                     <select
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                        value={tree.treeCategory || 'Natural'}
                        onChange={(e) => updateTreeEntry(index, 'treeCategory', e.target.value)}
                     >
                        <option value="Natural">Natural</option>
                        <option value="Planted">Planted</option>
                     </select>
                  ) : (
                     <Select
                        value={tree.treeCategory || 'Natural'}
                        onValueChange={(value) => updateTreeEntry(index, 'treeCategory', value)}
                     >
                        <SelectTrigger>
                           <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                           <SelectItem value="Natural">Natural</SelectItem>
                           <SelectItem value="Planted">Planted</SelectItem>
                        </SelectContent>
                     </Select>
                  )}
               </TableCell>
               <TableCell>
                  <Button
                     variant="ghost"
                     size="icon"
                     onClick={() => removeTreeEntry(index)}
                  >
                     <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
               </TableCell>
            </TableRow>
         );
      }
   };

   // Render totals row
   const renderTotalsRow = () => {
      if (isCuttingRequest) {
         return (
            <TableRow className="font-bold">
               <TableCell>Grand Total</TableCell>
               <TableCell>{findings?.tcebpFindings?.totalTrees || 0}</TableCell>
               <TableCell>{findings?.tcebpFindings?.totalGrossVolume?.toFixed(3) || '0.000'}</TableCell>
               <TableCell>{findings?.tcebpFindings?.totalNetVolume?.toFixed(3) || '0.000'}</TableCell>
               <TableCell></TableCell>
               <TableCell></TableCell>
            </TableRow>
         );
      } else {
         return (
            <TableRow className="font-bold">
               <TableCell>Grand Total</TableCell>
               <TableCell>{findings?.tcebpFindings?.totalTrees || 0}</TableCell>
               <TableCell></TableCell>
               <TableCell></TableCell>
               <TableCell></TableCell>
            </TableRow>
         );
      }
   };

   return (
      <div className="space-y-6">
         <div>
            <Label className="text-base font-semibold">
               Trees authorized for {isCuttingRequest ? 'Cutting' : 'Earth-balling'}
            </Label>
            <Table>
               <TableHeader>
                  {renderTableHeaders()}
               </TableHeader>
               <TableBody>
                  {findings?.tcebpFindings?.treeInventory?.map((tree, index) => renderTreeEntryRow(tree, index))}
                  {renderTotalsRow()}
               </TableBody>
            </Table>

            <Button
               type="button"
               variant="outline"
               className="mt-4"
               onClick={addTreeEntry}
            >
               <PlusCircle className="h-4 w-4 mr-2" />
               Add Tree Entry
            </Button>
         </div>
      </div>
   );
};

export default TCEBPInspectionFields;